import { useState, useEffect, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  RadioInputDiv,
  InputDiv,
  Card,
  Result,
  MathDiv,
  ActionButtonsRow,
  CopyHTMLButton,
  DesktopOnlyDiv,
  MobileOnlyDiv,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
  RadioWrapper,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function ConcreteBransonInertia() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [alphae, setAlphae] = useState(9.865)
  const [sectionType, setSectionType] = useState('rectangular')
  const [bf, setBf] = useState('')
  const [bw, setBw] = useState('')
  const [h, setH] = useState('')
  const [hf, setHf] = useState('')
  const [d, setD] = useState('')
  const [as, setAs] = useState('')
  const [asPrime, setAsPrime] = useState('')
  const [dPrime, setDPrime] = useState('')
  const [ma, setMa] = useState('')
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const HISTORY_KEY = 'concrete-branson-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setAlphae(entry.alphae)
      setSectionType(entry.sectionType || (entry.tShape ? 't' : 'rectangular'))
      setBf(entry.bf)
      setBw(entry.bw)
      setH(entry.h)
      setHf(entry.hf)
      setD(entry.d)
      setMa(entry.ma)
      setAs(entry.as)
      setAsPrime(entry.asPrime || '')
      setDPrime(entry.dPrime || '')
      setResult(entry.result)
      setData(entry.data)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.alphae = validateField('alphae', alphae)
    newErrors.bf = validateField('bf', bf)
    newErrors.h = validateField('h', h)
    newErrors.ma = validateField('ma', ma)
    newErrors.as = validateField('as', as)
    newErrors.d = validateField('d', d)

    if (sectionType !== 'rectangular') {
      newErrors.bw = validateField('bw', bw)
      newErrors.hf = validateField('hf', hf)
    }

    if (Number(asPrime) > 0) {
      newErrors.dPrime = validateField('dPrime', dPrime)
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, alphae, bf, bw, h, hf, ma, as, d, sectionType, asPrime, dPrime, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  // Suggests alpha_e (Es/Ecs) from fck per NBR 6118, assuming alpha_E = 1 (granito/gnaisse): first
  // the tangent modulus Eci, then the secant modulus Ecs = alpha_i * Eci, then Es/Ecs with
  // Es = 210000 MPa. Both fck and the moduli are in MPa.
  function suggestAlphaE(fckValue) {
    const fckNum = Number(fckValue)
    if (!fckNum || fckNum <= 0) return null
    const eci = fckNum <= 50 ? 5600 * Math.sqrt(fckNum) : 21500 * (fckNum / 10 + 1.25) ** (1 / 3)
    const alphaI = Math.min(0.8 + 0.2 * (fckNum / 80), 1)
    const ecs = alphaI * eci
    if (!ecs) return null
    return 210000 / ecs
  }

  const handleFckBlur = () => {
    handleBlur('fck')
    const suggested = suggestAlphaE(fck)
    if (suggested !== null) {
      setAlphae(Number(suggested.toFixed(3)))
      toast.info(
        'Relação Es/Ecs sugerida a partir do fck (NBR 6118), considerando α_E = 1 (agregado graúdo granito ou gnaisse). Ajuste manualmente se o agregado for outro.',
        { autoClose: 9000 }
      )
    }
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const isRectangular = sectionType === 'rectangular'

      const data = {
        fck,
        alphae,
        sectionType,
        bf,
        bw: isRectangular ? bf : bw,
        h,
        hf: isRectangular ? h : hf,
        d,
        as,
        asPrime: Number(asPrime) || 0,
        dPrime: Number(dPrime) || 0,
        ma,
      }

      setData(data)

      const response = await api.post('/concrete/branson-inertia', data)

      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        alphae,
        sectionType,
        bf,
        bw,
        h,
        hf,
        d,
        ma,
        as,
        asPrime,
        dPrime,
        data,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleSave() {
    const stateData = {
      fck,
      alphae,
      sectionType,
      bf,
      bw,
      h,
      hf,
      d,
      as,
      asPrime,
      dPrime,
      ma,
      isFormValid,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concreto inércia.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setAlphae(jsonData.alphae)
        setSectionType(jsonData.sectionType || (jsonData.tShape ? 't' : 'rectangular'))
        setBf(jsonData.bf)
        setBw(jsonData.bw)
        setH(jsonData.h)
        setHf(jsonData.hf)
        setD(jsonData.d)
        setAs(jsonData.as)
        setAsPrime(jsonData.asPrime || '')
        setDPrime(jsonData.dPrime || '')
        setMa(jsonData.ma)
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  const hasResult = result && result !== '' && result.section
  const hasCompressionSteel = hasResult && Number(data.asPrime) > 0
  const flangePosition = hasResult ? result.section.flangePosition : 'top'
  const isFlangeBottom = flangePosition === 'bottom'
  const region = hasResult ? result.x2Calc.region : null
  const hw = hasResult ? Number(data.h) - Number(data.hf) : null
  const yfSymbolic = isFlangeBottom ? 'h - \\dfrac{h_f}{2}' : '\\dfrac{h_f}{2}'
  const yfNumeric = hasResult ? (isFlangeBottom ? `${data.h} - \\dfrac{${data.hf}}{2}` : `\\dfrac{${data.hf}}{2}`) : ''

  const compressionAhSymbolic = hasCompressionSteel ? ` + A_s' \\cdot \\left(\\alpha_e - 1 \\right)` : ''
  const compressionAhNumeric = hasCompressionSteel ? ` + ${data.asPrime} \\cdot \\left(${data.alphae} - 1 \\right)` : ''
  const compressionYhSymbolic = hasCompressionSteel
    ? ` + \\dfrac{A_s' \\cdot \\left(\\alpha_e - 1 \\right) \\cdot d'}{A_h}`
    : ''
  const compressionYhNumeric =
    hasResult && hasCompressionSteel
      ? ` + \\dfrac{${data.asPrime} \\cdot \\left(${data.alphae} - 1 \\right) \\cdot ${data.dPrime}}{${result.ah.toFixed(2)}}`
      : ''
  const compressionIhSymbolic = hasCompressionSteel
    ? ` + A_s' \\cdot \\left(\\alpha_e - 1 \\right) \\cdot \\left(y_h - d' \\right)^2`
    : ''
  const compressionIhNumeric =
    hasResult && hasCompressionSteel
      ? ` + ${data.asPrime} \\cdot \\left(${data.alphae} - 1 \\right) \\cdot \\left(${result.yh.toFixed(2)} - ${data.dPrime} \\right)^2`
      : ''

  const steelTermSymbolic = hasCompressionSteel
    ? `+ \\alpha_e \\cdot A_s' \\cdot \\left(x_{II} - d' \\right) - \\alpha_e \\cdot A_s \\cdot \\left(d - x_{II} \\right)`
    : `- \\alpha_e \\cdot A_s \\cdot \\left(d - x_{II} \\right)`
  const steelTermNumeric =
    hasResult && hasCompressionSteel
      ? `+ ${data.alphae} \\cdot ${data.asPrime} \\cdot \\left(x_{II} - ${data.dPrime} \\right) - ${data.alphae} \\cdot ${data.as} \\cdot \\left(${data.d} - x_{II} \\right)`
      : hasResult
        ? `- ${data.alphae} \\cdot ${data.as} \\cdot \\left(${data.d} - x_{II} \\right)`
        : ''

  const i2SteelSymbolic = hasCompressionSteel
    ? `\\alpha_e \\cdot A_s \\cdot \\left(d - x_{II}\\right)^2 + \\alpha_e \\cdot A_s' \\cdot \\left(x_{II} - d'\\right)^2`
    : `\\alpha_e \\cdot A_s \\cdot \\left(d - x_{II}\\right)^2`
  const i2SteelNumeric = hasResult
    ? hasCompressionSteel
      ? `${data.alphae} \\cdot ${data.as} \\cdot \\left(${data.d} - ${result.x2Calc.x2.toFixed(2)}\\right)^2 + ${data.alphae} \\cdot ${data.asPrime} \\cdot \\left(${result.x2Calc.x2.toFixed(2)} - ${data.dPrime}\\right)^2`
      : `${data.alphae} \\cdot ${data.as} \\cdot \\left(${data.d} - ${result.x2Calc.x2.toFixed(2)}\\right)^2`
    : ''

  let concreteEqSymbolic = ''
  let concreteEqNumeric = ''
  let concreteI2Symbolic = ''
  let concreteI2Numeric = ''
  let regionCaseLabel = ''

  if (hasResult) {
    const x2 = result.x2Calc.x2.toFixed(2)

    if (region === 'rectangular') {
      regionCaseLabel = 'Seção retangular — a área comprimida é retangular em toda a altura útil.'
      concreteEqSymbolic = `\\dfrac{b \\cdot {x_{II}}^2}{2}`
      concreteEqNumeric = `\\dfrac{${data.bf} \\cdot {x_{II}}^2}{2}`
      concreteI2Symbolic = `\\dfrac{b \\cdot {x_{II}}^3}{3}`
      concreteI2Numeric = `\\dfrac{${data.bf} \\cdot {${x2}}^3}{3}`
    } else if (region === 'flange' && !isFlangeBottom) {
      regionCaseLabel = `Linha neutra dentro da mesa: x_II ≤ hf (hf = ${data.hf} cm).`
      concreteEqSymbolic = `\\dfrac{b_f \\cdot {x_{II}}^2}{2}`
      concreteEqNumeric = `\\dfrac{${data.bf} \\cdot {x_{II}}^2}{2}`
      concreteI2Symbolic = `\\dfrac{b_f \\cdot {x_{II}}^3}{3}`
      concreteI2Numeric = `\\dfrac{${data.bf} \\cdot {${x2}}^3}{3}`
    } else if (region === 'web' && !isFlangeBottom) {
      regionCaseLabel = `Linha neutra dentro da alma: x_II > hf (hf = ${data.hf} cm).`
      concreteEqSymbolic = `\\dfrac{b_w \\cdot {x_{II}}^2}{2} + \\left(b_f - b_w \\right) \\cdot h_f \\cdot \\left(x_{II} - \\dfrac{h_f}{2} \\right)`
      concreteEqNumeric = `\\dfrac{${data.bw} \\cdot {x_{II}}^2}{2} + \\left(${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} \\cdot \\left(x_{II} - \\dfrac{${data.hf}}{2} \\right)`
      concreteI2Symbolic = `\\dfrac{b_w \\cdot {x_{II}}^3}{3} + \\left(b_f - b_w \\right) \\cdot \\left[\\dfrac{{h_f}^3}{12} + h_f \\cdot \\left(x_{II} - \\dfrac{h_f}{2} \\right)^2 \\right]`
      concreteI2Numeric = `\\dfrac{${data.bw} \\cdot {${x2}}^3}{3} + \\left(${data.bf} - ${data.bw} \\right) \\cdot \\left[\\dfrac{{${data.hf}}^3}{12} + ${data.hf} \\cdot \\left(${x2} - \\dfrac{${data.hf}}{2} \\right)^2 \\right]`
    } else if (region === 'web' && isFlangeBottom) {
      regionCaseLabel = `T invertido — mesa inferior tracionada, não participa: x_II ≤ hw (hw = h - hf = ${hw} cm).`
      concreteEqSymbolic = `\\dfrac{b_w \\cdot {x_{II}}^2}{2}`
      concreteEqNumeric = `\\dfrac{${data.bw} \\cdot {x_{II}}^2}{2}`
      concreteI2Symbolic = `\\dfrac{b_w \\cdot {x_{II}}^3}{3}`
      concreteI2Numeric = `\\dfrac{${data.bw} \\cdot {${x2}}^3}{3}`
    } else if (region === 'flange' && isFlangeBottom) {
      regionCaseLabel = `T invertido — linha neutra dentro da mesa inferior: x_II > hw (hw = h - hf = ${hw} cm).`
      concreteEqSymbolic = `\\dfrac{b_w \\cdot {x_{II}}^2}{2} + \\left(b_f - b_w \\right) \\cdot \\dfrac{\\left(x_{II} - h_w \\right)^2}{2}`
      concreteEqNumeric = `\\dfrac{${data.bw} \\cdot {x_{II}}^2}{2} + \\left(${data.bf} - ${data.bw} \\right) \\cdot \\dfrac{\\left(x_{II} - ${hw} \\right)^2}{2}`
      concreteI2Symbolic = `\\dfrac{b_w \\cdot {x_{II}}^3}{3} + \\left(b_f - b_w \\right) \\cdot \\dfrac{\\left(x_{II} - h_w \\right)^3}{3}`
      concreteI2Numeric = `\\dfrac{${data.bw} \\cdot {${x2}}^3}{3} + \\left(${data.bf} - ${data.bw} \\right) \\cdot \\dfrac{\\left(${x2} - ${hw} \\right)^3}{3}`
    }
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Inércia Equivalente</span>
          </div>
          <h1>📏 Inércia Equivalente (Branson)</h1>
          <p className="subtitle">
            Cálculo da inércia equivalente de seções de concreto armado fissuradas pelo método de Branson
          </p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🔬 Método de Branson</Badge>
            <Badge>📐 Estádios I e II</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta calcula a inércia equivalente de seções de concreto armado fissuradas utilizando o método
              de Branson, conforme a NBR 6118:2014.
            </p>
            <p>
              O cálculo considera a seção homogeneizada (Estádio I) e a seção fissurada (Estádio II), fornecendo a
              inércia equivalente que pondera os dois estados de acordo com o momento atuante e o momento de fissuração.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>Método de Branson</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Inércia equivalente</h2>
          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Materiais</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleFckBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Relação do Módulo de Elasticidade do Aço e do Concreto</span>
              <ValidatedInput
                name="alphae"
                type="number"
                value={alphae || ''}
                onChange={(event) => setAlphae(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <RadioInputDiv>
              <span>Formato</span>
              <div>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-rectangle"
                    value="rectangular"
                    checked={sectionType === 'rectangular'}
                    onChange={() => setSectionType('rectangular')}
                  />
                  <span>Retangular</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-t"
                    value="t"
                    checked={sectionType === 't'}
                    onChange={() => setSectionType('t')}
                  />
                  <span>T</span>
                </RadioWrapper>
                <RadioWrapper>
                  <input
                    type="radio"
                    name="shape"
                    id="shape-t-invertido"
                    value="t-invertido"
                    checked={sectionType === 't-invertido'}
                    onChange={() => setSectionType('t-invertido')}
                  />
                  <span>T invertido</span>
                </RadioWrapper>
              </div>
            </RadioInputDiv>
            {sectionType === 'rectangular' ? (
              <>
                <InputDiv>
                  <span>Largura (cm)</span>
                  <ValidatedInput
                    name="bf"
                    type="number"
                    value={bf || ''}
                    onChange={(event) => setBf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Altura (cm)</span>
                  <ValidatedInput
                    name="h"
                    type="number"
                    value={h || ''}
                    onChange={(event) => setH(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            ) : (
              <>
                <p style={{ fontSize: '90%', opacity: 0.8 }}>
                  {sectionType === 't'
                    ? 'A mesa fica na face superior (comprimida).'
                    : 'A mesa fica na face inferior. A compressão continua sendo considerada na face superior.'}
                </p>
                <InputDiv>
                  <span>Largura da mesa (cm)</span>
                  <ValidatedInput
                    name="bf"
                    type="number"
                    value={bf || ''}
                    onChange={(event) => setBf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Largura da Alma (cm)</span>
                  <ValidatedInput
                    name="bw"
                    type="number"
                    value={bw || ''}
                    onChange={(event) => setBw(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Altura total (cm)</span>
                  <ValidatedInput
                    name="h"
                    type="number"
                    value={h || ''}
                    onChange={(event) => setH(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
                <InputDiv>
                  <span>Espessura da mesa (cm)</span>
                  <ValidatedInput
                    name="hf"
                    type="number"
                    value={hf || ''}
                    onChange={(event) => setHf(Number(event.target.value))}
                    errors={errors}
                    touched={touched}
                    onBlur={handleBlur}
                    disabled={isLoading}
                  />
                </InputDiv>
              </>
            )}
            <InputDiv>
              <span>Altura útil (cm) — distância do topo ao centro da armadura tracionada</span>
              <ValidatedInput
                name="d"
                type="number"
                value={d || ''}
                onChange={(event) => setD(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Momento Atuante (kN.cm)</span>
              <ValidatedInput
                name="ma"
                type="number"
                value={ma || ''}
                onChange={(event) => setMa(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Considerações</h3>
            <InputDiv>
              <span>Área de aço longitudinal tracionado As (cm²)</span>
              <ValidatedInput
                name="as"
                type="number"
                value={as || ''}
                onChange={(event) => setAs(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Área de aço comprimido As&apos; (cm²) — opcional</span>
              <ValidatedInput
                name="asPrime"
                type="number"
                value={asPrime}
                onChange={(event) => setAsPrime(event.target.value)}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            {Number(asPrime) > 0 && (
              <InputDiv>
                <span>Profundidade d&apos; (cm) — distância do topo ao centro da armadura comprimida</span>
                <ValidatedInput
                  name="dPrime"
                  type="number"
                  value={dPrime}
                  onChange={(event) => setDPrime(event.target.value)}
                  errors={errors}
                  touched={touched}
                  onBlur={handleBlur}
                  disabled={isLoading}
                />
              </InputDiv>
            )}
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <MathDiv>
                <BlockMath math={`I_{I} = ${result.ih.toFixed(1)} \\, \\text{ cm}^4`} />
                <BlockMath math={`I_{II} = ${result.i2.toFixed(1)} \\, \\text{ cm}^4`} />
                <BlockMath math={`I_{eq} = ${result.ieq.toFixed(1)} \\, \\text{ cm}^4`} />
              </MathDiv>
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Seção homogeneizada</h3>
              <MathDiv>
                <div style={{ fontSize: '90%', opacity: 0.8 }}>
                  <p>
                    A compressão é sempre considerada na face superior. Seção:{' '}
                    {region === 'rectangular' || data.sectionType === 'rectangular'
                      ? 'retangular'
                      : isFlangeBottom
                        ? 'T invertido (mesa na face inferior)'
                        : 'T (mesa na face superior)'}
                    .
                  </p>
                  {data.sectionType !== 'rectangular' && (
                    <>
                      <p>Centroide da mesa:</p>
                      <BlockMath math={`y_f = ${yfSymbolic} = ${yfNumeric} \\text{ cm}`} />
                    </>
                  )}
                </div>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`A_{h} = \\left( b_f - b_w \\right) \\cdot h_f + b_w \\cdot h + A_s \\cdot \\left(\\alpha_e - 1 \\right)${compressionAhSymbolic}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath math={`A_{h} = \\left( b_f - b_w \\right) \\cdot h_f + b_w \\cdot h + \\cdots`} />
                  <BlockMath math={`\\cdots + A_s \\cdot \\left(\\alpha_e - 1 \\right)${compressionAhSymbolic}`} />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`A_{h} = \\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} + ${data.bw} \\cdot ${data.h} + ${data.as} \\cdot \\left(${data.alphae} - 1 \\right)${compressionAhNumeric} = ${result.ah.toFixed(2)} \\text{ cm}^2`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`A_{h} = \\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} + ${data.bw} \\cdot ${data.h} + \\cdots`}
                  />
                  <BlockMath
                    math={`\\cdots + ${data.as} \\cdot \\left(${data.alphae} - 1 \\right)${compressionAhNumeric} = ${result.ah.toFixed(2)} \\text{ cm}^2`}
                  />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`y_h = \\dfrac{\\left( b_f - b_w \\right) \\cdot h_f \\cdot y_f+b_w \\cdot \\dfrac{h^2}{2}}{A_h} + \\dfrac{A_s \\cdot \\left(\\alpha_e - 1 \\right) \\cdot d}{A_h}${compressionYhSymbolic}`}
                  />
                  <BlockMath
                    math={`y_h = \\dfrac{\\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} \\cdot \\left(${yfNumeric}\\right)+${data.bw} \\cdot \\dfrac{(${data.h})^2}{2}}{A_h} + \\dfrac{${data.as} \\cdot \\left(${data.alphae} - 1 \\right) \\cdot ${data.d}}{${result.ah.toFixed(2)}}${compressionYhNumeric} = ${result.yh.toFixed(2)} \\text{ cm}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`y_h = \\dfrac{\\left( b_f - b_w \\right) \\cdot h_f \\cdot y_f+b_w \\cdot \\dfrac{h^2}{2}}{A_h} + \\cdots`}
                  />
                  <BlockMath
                    math={`\\cdots + \\dfrac{A_s \\cdot \\left(\\alpha_e - 1 \\right) \\cdot d}{A_h}${compressionYhSymbolic}`}
                  />
                  <div style={{ fontSize: '90%' }}>
                    <BlockMath
                      math={`y_h = \\dfrac{\\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} \\cdot \\left(${yfNumeric}\\right)+${data.bw} \\cdot \\dfrac{(${data.h})^2}{2}}{A_h} + \\cdots`}
                    />
                  </div>
                  <BlockMath
                    math={`\\cdots + \\dfrac{${data.as} \\cdot \\left(${data.alphae} - 1 \\right) \\cdot ${data.d}}{${result.ah.toFixed(2)}}${compressionYhNumeric} = ${result.yh.toFixed(2)} \\text{ cm}`}
                  />
                </MobileOnlyDiv>
                <DesktopOnlyDiv>
                  <BlockMath
                    math={`I_h = \\dfrac{\\left(b_f - b_w \\right) \\cdot {h_f}^3}{12} + \\dfrac{b_w \\cdot h^3}{12} + \\left( b_f - b_w \\right) \\cdot h_f \\cdot \\left( y_h - y_f \\right)^2 + b_w \\cdot h \\cdot \\left( y_h - \\dfrac{h}{2} \\right)^2 + A_s \\cdot \\left( \\alpha_e - 1 \\right) \\cdot \\left(y_h - d \\right)^2${compressionIhSymbolic}`}
                  />
                  <BlockMath
                    math={`I_h = \\dfrac{\\left(${data.bf} - ${data.bw} \\right) \\cdot ${data.hf}^3}{12} + \\dfrac{${data.bw} \\cdot ${data.h}^3}{12} + \\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} \\cdot \\left( ${result.yh.toFixed(2)} - \\left(${yfNumeric}\\right) \\right)^2 + ${data.bw} \\cdot ${data.h} \\cdot \\left( ${result.yh.toFixed(2)} - \\dfrac{ ${data.h} }{2} \\right)^2 + \\cdots`}
                  />
                  <BlockMath
                    math={`\\cdots + ${data.as} \\cdot \\left( ${data.alphae} - 1 \\right) \\cdot \\left(${result.yh.toFixed(2)} - ${data.d} \\right)^2${compressionIhNumeric}`}
                  />
                </DesktopOnlyDiv>
                <MobileOnlyDiv>
                  <BlockMath
                    math={`I_h = \\dfrac{\\left(b_f - b_w \\right) \\cdot {h_f}^3}{12} + \\dfrac{b_w \\cdot h^3}{12} + \\cdots`}
                  />
                  <BlockMath
                    math={`\\cdots + \\left( b_f - b_w \\right) \\cdot h_f \\cdot \\left( y_h - y_f \\right)^2 + \\cdots`}
                  />
                  <BlockMath math={`\\cdots + b_w \\cdot h \\cdot \\left( y_h - \\dfrac{h}{2} \\right)^2 + \\cdots`} />
                  <BlockMath
                    math={`\\cdots + A_s \\cdot \\left( \\alpha_e - 1 \\right) \\cdot \\left(y_h - d \\right)^2${compressionIhSymbolic}`}
                  />
                  <div style={{ fontSize: '85%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <BlockMath
                      math={`I_h = \\dfrac{\\left(${data.bf} - ${data.bw} \\right) \\cdot ${data.hf}^3}{12} + \\dfrac{${data.bw} \\cdot ${data.h}^3}{12} + \\cdots`}
                    />
                    <BlockMath
                      math={`\\cdots + \\left( ${data.bf} - ${data.bw} \\right) \\cdot ${data.hf} \\cdot \\left( ${result.yh.toFixed(2)} - \\left(${yfNumeric}\\right) \\right)^2 + \\cdots`}
                    />
                  </div>
                  <BlockMath
                    math={`\\cdots + ${data.bw} \\cdot ${data.h} \\cdot \\left( ${result.yh.toFixed(2)} - \\dfrac{ ${data.h} }{2} \\right)^2 + \\cdots`}
                  />
                  <BlockMath
                    math={`\\cdots + ${data.as} \\cdot \\left( ${data.alphae} - 1 \\right) \\cdot \\left(${result.yh.toFixed(2)} - ${data.d} \\right)^2${compressionIhNumeric}`}
                  />
                </MobileOnlyDiv>

                <BlockMath math={`\\boxed{I_h = ${result.ih.toFixed(2)} \\text{ cm}^4}`} />
              </MathDiv>
              <h3>Momento de fissuração</h3>
              <MathDiv>
                <BlockMath math={`f_{ct,m} = 0,3 \\cdot {f_{ck}}^{\\frac{2}{3}}`} />
                <BlockMath
                  math={`f_{ct,m} = 0,3 \\cdot {${data.fck} }^{\\frac{2}{3}} = ${result.fct.toFixed(3)} \\text{ kN/cm}^2`}
                />
                <BlockMath math={`y_t = h - y_h`} />
                <BlockMath math={`y_t = ${data.h} - ${result.yh.toFixed(3)} = ${result.yt.toFixed(3)} \\text{ cm}`} />
                <BlockMath math={`M_r = \\dfrac{\\alpha \\cdot f_{ct,m} \\cdot I_h}{y_t}`} />
                <BlockMath
                  math={`M_r = \\dfrac{${result.alpha2} \\cdot ${result.fct.toFixed(3)} \\cdot ${result.ih.toFixed(2)}}{${result.yt.toFixed(3)}}`}
                />
                <BlockMath math={`\\boxed{M_r = ${result.mr.toFixed(2)} \\text{ kN.cm}}`} />
                {result.checkMa ? (
                  <>
                    <BlockMath math={`M_r \\geq Ma`} />
                    <BlockMath math={`I = I_h = ${result.ih.toFixed(2)} \\text{ cm}^4`} />
                  </>
                ) : null}
              </MathDiv>
              {!result.checkMa && (
                <>
                  <h3>Seção do estádio II</h3>
                  <MathDiv>
                    <div style={{ fontSize: '90%', opacity: 0.8 }}>
                      <p>Hipótese geométrica verificada: {regionCaseLabel}</p>
                    </div>
                    <BlockMath math={`${concreteEqSymbolic} ${steelTermSymbolic} = 0`} />
                    <DesktopOnlyDiv>
                      <BlockMath math={`${concreteEqNumeric} ${steelTermNumeric} = 0`} />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`${concreteEqNumeric} + \\cdots`} />
                      <BlockMath math={`\\cdots ${steelTermNumeric} = 0`} />
                    </MobileOnlyDiv>
                    <BlockMath
                      math={`a = ${result.x2Calc.a.toFixed(3)} \\qquad b = ${result.x2Calc.b.toFixed(3)} \\qquad c = ${result.x2Calc.c.toFixed(3)}`}
                    />
                    <BlockMath
                      math={`x_{II} = \\dfrac{-b \\pm \\left(b^2 - 4 \\cdot a \\cdot c \\right)^{1/2}}{2 \\cdot a} \\qquad \\Delta = ${result.x2Calc.delta.toFixed(3)}`}
                    />
                    <BlockMath>{String.raw`x_{II} = \begin{cases} ${result.x2Calc.x2_1.toFixed(3)} \text{ cm} \\ ${result.x2Calc.x2_2.toFixed(3)} \text{ cm} \end{cases}`}</BlockMath>
                    <BlockMath
                      math={`0 < x_{II} < d \\; \\Rightarrow \\; 0 < ${result.x2Calc.x2.toFixed(3)} < ${data.d} \\; \\checkmark`}
                    />
                    <BlockMath math={`x_{II} = ${result.x2Calc.x2.toFixed(2)} \\text{ cm}`} />
                    <BlockMath math={`I_{II} = ${concreteI2Symbolic} + ${i2SteelSymbolic}`} />
                    <DesktopOnlyDiv>
                      <BlockMath math={`I_{II} = ${concreteI2Numeric} + ${i2SteelNumeric}`} />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath math={`I_{II} = ${concreteI2Numeric} + \\cdots`} />
                      <BlockMath math={`\\cdots + ${i2SteelNumeric}`} />
                    </MobileOnlyDiv>
                    <BlockMath math={`\\boxed{I_{II} = ${result.i2.toFixed(2)} \\text{ cm}^4}`} />
                    <BlockMath
                      math={`I_{eq} = \\left(\\dfrac{M_r}{M_a}\\right)^3 \\cdot I_h + \\left[1 - \\left(\\dfrac{M_r}{M_a}\\right)^3 \\right] \\cdot I_{II}`}
                    />
                    <DesktopOnlyDiv>
                      <BlockMath
                        math={`I_{eq} = \\left(\\dfrac{${result.mr.toFixed(1)}}{${data.ma}}\\right)^3 \\cdot ${result.ih.toFixed(1)} + \\left[1 - \\left(\\dfrac{${result.mr.toFixed(1)}}{${data.ma}}\\right)^3 \\right] \\cdot ${result.i2.toFixed(1)}`}
                      />
                    </DesktopOnlyDiv>
                    <MobileOnlyDiv>
                      <BlockMath
                        math={`I_{eq} = \\left(\\dfrac{${result.mr.toFixed(1)}}{${data.ma}}\\right)^3 \\cdot ${result.ih.toFixed(1)} + \\cdots`}
                      />
                      <BlockMath
                        math={`\\cdots + \\left[1 - \\left(\\dfrac{${result.mr.toFixed(1)}}{${data.ma}}\\right)^3 \\right] \\cdot ${result.i2.toFixed(1)}`}
                      />
                    </MobileOnlyDiv>
                    <BlockMath math={`\\boxed{I_{eq} = ${result.ieq.toFixed(3)} \\text{ cm}^4}`} />
                  </MathDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
